import { DeveloperMenuSettings, Device } from  'model';
import { DeviceType, Measure, ValveState, DeviceSystemMode } from 'model/Device';
import { UnitSystem, UnitConverter, UnitFormat } from 'model';
import { ConverterFn, FormatterFn } from 'model/UnitSystem';
import { SystemMode } from 'model/SystemMode';
import { PESResponse } from './Threshhold';

export type SystemModeValues = 'home' | 'away' | 'sleep';

export interface DeviceSystemModeResponse {
  isLocked: boolean;
  shouldInherit: boolean;
  lastKnown: SystemModeValues;
  target: SystemModeValues;
  revertMode?: SystemModeValues;
  revertMinutes?: number;
}

export interface DeviceSystemModePayload {
  target: SystemModeValues;
  revertMode: SystemModeValues;
  revertMinutes: number;
  isLocked: boolean;
}

type ValveValues = 'open' | 'closed' | 'in_transition' | 'unknown';

export interface DeviceValveResponse {
  target: ValveValues;
  lastKnown: ValveValues;
}

export interface DeviceValvePayload {
  target: ValveValues;
}

export interface CurrentTelemetryResponse {
  gpm?: number;
  psi?: number;
  tempF?: number;
  humidity?: number;
  updated?: string;
}

export interface DeviceTelemetryResponse {
  current?: CurrentTelemetryResponse;
}

export interface BatteryResponse {
  level?: number;
  updated?: string;
}

interface NotificationStatsResponse {
  infoCount: number;
  warningCount: number;
  criticalCount: number;
}

interface NotificationsResponse {
  pending: NotificationStatsResponse
}

export interface DeviceResponse {
  id: string;
  isConnected: boolean;
  macAddress: string;
  nickname: string;
  deviceModel: string;
  deviceType: string;
  isPaired?: boolean;
  systemMode: DeviceSystemModeResponse;
  valve: DeviceValveResponse;
  telemetry: DeviceTelemetryResponse;
  battery?: BatteryResponse;
  notifications: NotificationsResponse;
  pes?: PESResponse;
  hardwareThresholds: DeviceHardwareThresholds
}

export interface DeviceHardwareThresholds {
  tempF: HardwareThresholds,
  tempC: HardwareThresholds,
  humidity: HardwareThresholds,
  battery: HardwareThresholds,
  tempEnabled: boolean,
  humidityEnabled: boolean,
  batteryEnabled: boolean
}

export interface HardwareThresholds{
  okMin: number;
  okMax: number;
  minValue: number;
  maxValue: number;
}

export interface DevicePayload {
  valve?: DeviceValvePayload;
}

export interface UpdateDevicePayload {
  nickname?: string,
  hardwareThresholds?: DeviceHardwareThresholds
}

export interface FwPropertiesDevicePayload {
  playerAction: string;
  playerFlow: number;
  playerPressure: number;
  playerMinPressure: number;
  playerTemperature: number;
}

export const toDeviceType = (deviceType: string): DeviceType => {
  switch (deviceType) {
    case 'flo_device_v2':
      return DeviceType.SHUTOFF;
    case 'puck_oem':
      return DeviceType.DETECTOR;
    default:
      return DeviceType.SHUTOFF;
  }
}

export const toSystemMode = (systemMode: SystemModeValues): SystemMode => {
  switch(systemMode) {
    case 'home':
      return SystemMode.HOME;
    case 'sleep':
      return SystemMode.SLEEP;
    case 'away':
      return SystemMode.AWAY;
    default:
      return SystemMode.HOME;
  }
}

export const fromSystemMode = (systemMode: SystemMode): SystemModeValues => {
  switch(systemMode) {
    case SystemMode.HOME:
      return 'home';
    case SystemMode.SLEEP:
      return 'sleep';
    case SystemMode.AWAY:
      return 'away';
    default:
      return 'home';
  }
}

const toValveState = (valveState: ValveValues): ValveState => {
  switch(valveState) {
    case 'open':
      return ValveState.OPEN;
    case 'closed':
      return ValveState.CLOSED;
    case 'in_transition':
      return ValveState.IN_TRANSITION;
    case 'unknown':
    default:
      return ValveState.UNKNOWN;
  }
}

export const fromValveState = (valveState: ValveState): ValveValues => {
  switch(valveState) {
    case ValveState.OPEN:
      return 'open';
    case ValveState.CLOSED:
      return 'closed';
    default:
      return 'unknown';
  }
}

export const toMeasure = (unit: UnitSystem, value: number | undefined, converter: ConverterFn, longFormatter: FormatterFn, shortFormatter: FormatterFn): Measure => ({
  unit,
  long: longFormatter(unit),
  short: shortFormatter(unit),
  value: Math.round(converter(value || 0, unit) * 10) / 10,
});

export const toDevice = (response: DeviceResponse, unit: UnitSystem): Device => ({
  id: response.id,
  isConnected: response.isConnected,
  macAddress: response.macAddress,
  nickname: response.nickname,
  type: toDeviceType(response.deviceType),
  systemMode: {
    isLocked: response.systemMode.isLocked,
    lastKnown: toSystemMode(response.systemMode.lastKnown),
    shouldInherit: response.systemMode.shouldInherit,
    target: toSystemMode(response.systemMode.target),
    revertMinutes: response.systemMode.revertMinutes,
    revertMode: response.systemMode.revertMode && toSystemMode(response.systemMode.revertMode)
  },
  valve: {
    lastKnown: toValveState(response.valve.lastKnown),
    target: toValveState(response.valve.target)
  },
  isPaired: response.isPaired,
  telemetry: {
    updated: response.telemetry.current?.updated || '2000-01-01T00:00:00Z',
    temp: toMeasure(unit, response.telemetry.current?.tempF, UnitConverter.convertFromFahrenheit, UnitFormat.displayLongTempUnit, UnitFormat.displayShortTempUnit),
    flow: toMeasure(unit, response.telemetry.current?.gpm, UnitConverter.convertFromGallons, UnitFormat.displayLongFlowUnit, UnitFormat.displayShortFlowUnit),
    pressure: toMeasure(unit, response.telemetry.current?.psi, UnitConverter.convertFromPSI, UnitFormat.displayLongPressureUnit, UnitFormat.displayShortPressureUnit),
    battery: toMeasure(unit, response.battery?.level, (x, _) => x, UnitFormat.displayLongBatteryUnit, UnitFormat.displayShortBatteryUnit),
    humidity: toMeasure(unit, response.telemetry.current?.humidity, (x, _) => x, UnitFormat.displayLongHumidityUnit, UnitFormat.displayShortHumidityUnit),
  },
  notifications: {
    pending: {
      criticalCount: response.notifications.pending.criticalCount || 0,
      warningCount: response.notifications.pending.warningCount || 0,
      infoCount: response.notifications?.pending.infoCount || 0,
    }
  },
});

export const fromDeviceSystemMode = (systemMode: DeviceSystemMode): DeviceSystemModePayload => ({
  isLocked: systemMode.isLocked || false,
  target: fromSystemMode(systemMode.target || SystemMode.HOME),
  revertMinutes: systemMode.revertMinutes || 2 * 60, // default 2 hours
  revertMode: fromSystemMode(systemMode.revertMode || SystemMode.HOME),
});


export const fromPartialDevice = (device: Partial<Device>): UpdateDevicePayload => {
  return {
    nickname: device?.nickname,
    hardwareThresholds: device?.hardwareThresholds
  };
}

export const toDeveloperMenuSettings = (device: Partial<Device>): DeveloperMenuSettings => {
  return {
    playerAction: device?.fwProperties?.player_action || "",
    playerFlow: device?.fwProperties?.player_flow || 0,
    playerPressure: device?.fwProperties?.player_pressure || 0,
    playerMinPressure: device?.fwProperties?.player_min_pressure || 0,
    playerTemperature: device?.fwProperties?.player_temperature || 0,
  };
}
